import { Alignment, Button, InputGroup, Tooltip } from '@blueprintjs/core';
import BOSDateInput from 'Components/Generic/BOSDateInput';
import BOSSelect from 'Components/Generic/BOSSelect';
import { showSaveSuccessToast } from 'Components/Generic/BOSToasterUtil';
import AddNewButton from 'Components/Generic/Buttons/AddNewButton';
import ButtonFooter from 'Components/Generic/Buttons/ButtonFooter';
import DeleteButton from 'Components/Generic/Buttons/DeleteButton';
import DuplicateButton from 'Components/Generic/Buttons/DuplicateButton';
import SaveButton from 'Components/Generic/Buttons/SaveButton';
import UndoButton from 'Components/Generic/Buttons/UndoButton';
import NoContentBox from 'Components/Generic/Misc/NoContentBox';
import ChargeAccountData from 'Data/ChargeAccountData';
import { useAlertContext } from 'Data/context/Alerts';
import { useViewportSizer } from 'Data/context/ViewportSizer';
import { addYears } from 'date-fns';
import DataManager from 'Lib/DataManager';
import {
  getTransactionCategory,
  TRANSACTION_CATEGORY,
} from 'Pages/Transactions/Enum';
import PropTypes from 'prop-types';
import { useEffect, useRef, useState } from 'react';
import { areEquivalent } from 'Util';
import { formatDate } from 'Util/dateUtil';
import styles from '../ChargeAccounts/AccountsTable.module.css';

let DM = new DataManager();

const AccountsTable = ({
  orgId,
  selectedAccount,
  editable = false,
  compact = false,
  fieldsForNewAccount = {},
  onUpdateAccounts = () => {},
  onSelectAccount,
}) => {
  const { narrowScreen } = useViewportSizer();

  const { openDeleteDialog, showDeletedToast } = useAlertContext();

  const [accountList, setAccountList] = useState([]);
  const [hasBeenUpdated, setHasBeenUpdated] = useState(false);
  const orgIdRef = useRef();
  const newAccountFieldValuesRef = useRef();

  useEffect(() => {
    DM.add(
      ChargeAccountData.registerListener(
        'CA_FOR_CORE_LIST_LOADED',
        onAccountListLoaded,
      ),
      ChargeAccountData,
    );
    DM.add(
      ChargeAccountData.registerListener('RECORDS_LOADED', onAccountListLoaded),
      ChargeAccountData,
    );
    DM.add(
      ChargeAccountData.registerListener(
        'RECORDS_LOAD_ERROR',
        onAccountListLoadError,
      ),
      ChargeAccountData,
    );
    DM.add(
      ChargeAccountData.registerListener(
        'BLANK_RECORD_LOADED',
        onBlankAccountLoaded,
      ),
      ChargeAccountData,
    );
    DM.add(
      ChargeAccountData.registerListener('RECORDS_UPDATED', onAccountsUpdated),
      ChargeAccountData,
    );
    DM.add(
      ChargeAccountData.registerListener('RECORDS_DELETED', onAccountDeleted),
      ChargeAccountData,
    );
    return () => {
      DM.clear();
    };
  }, []);

  useEffect(() => {
    orgIdRef.current = orgId;
    fetchAccounts();
  }, [orgId]);

  useEffect(() => {
    newAccountFieldValuesRef.current = fieldsForNewAccount;
  }, [fieldsForNewAccount]);

  const fetchAccounts = () => {
    if (orgIdRef.current) {
      ChargeAccountData.getRecordList(
        {
          where: { '*parent_organization_id': orgIdRef.current },
          _sort: 'category,org,fund,code',
        },
        true,
        'CA_FOR_CORE_LIST_LOADED',
      );
    } else {
      setAccountList([]);
    }
  };

  const onAccountListLoaded = (payload) => {
    if (payload) {
      setAccountList(payload.records);
      setHasBeenUpdated(false);
    }
  };

  const onAccountListLoadError = () => {};

  const onBlankAccountLoaded = (newAccount) => {
    setHasBeenUpdated(true);
    let updatedAccount = {
      ...newAccount,
      valid_to: formatDate(new Date()),
      valid_from: formatDate(new Date()),
      ...newAccountFieldValuesRef.current,
    };
    setAccountList((prevAccountList) => [...prevAccountList, updatedAccount]);
  };

  const onAccountsUpdated = (payload) => {
    if (payload) {
      showSaveSuccessToast('Accounts Saved');
      fetchAccounts();
      onUpdateAccounts();
    }
  };

  const duplicateAccount = (newAccount) => {
    setHasBeenUpdated(true);
    let updatedAccount = {
      category: newAccount.category,
      code: newAccount.code,
      fund: newAccount.fund,
      name: newAccount.name,
      notes: newAccount.notes,
      org: newAccount.org,
      valid_to: formatDate(new Date()),
      valid_from: formatDate(new Date()),
      ...newAccountFieldValuesRef.current,
    };
    setAccountList((prevLineItems) => [...prevLineItems, updatedAccount]);
  };

  const updateItemValue = (itemToUpdate, updateField, updatedValue) => {
    const updatedArray = accountList.map((item) => {
      if (item === itemToUpdate) {
        let originalItem = { ...item };
        let updatedItem = { ...item, [updateField]: updatedValue };
        if (!areEquivalent(originalItem, updatedItem)) {
          setHasBeenUpdated(true);
        }
        return updatedItem;
      }
      return item;
    });
    setAccountList(updatedArray);
  };

  const saveClicked = () => {
    ChargeAccountData.updateOrCreateAccountsForOrg(accountList, orgId);
  };

  const deleteClicked = (account) => {
    if (account.id) {
      openDeleteDialog({
        objectType: 'Account',
        onConfirmDelete: () => ChargeAccountData.deleteAccount(account),
      });
    } else {
      setAccountList((prevAccounts) =>
        prevAccounts.filter((item) => item !== account),
      );
    }
  };

  const onAccountDeleted = () => {
    showDeletedToast('Account');
    fetchAccounts();
  };

  const undoChanges = () => {
    fetchAccounts();
  };

  let headers = (
    <tr>
      {onSelectAccount && (
        <td
          style={{
            ...(!compact &&
              !narrowScreen &&
              accountList?.length && { minWidth: 140 }),
          }}
        />
      )}
      {!compact && (
        <td style={{ minWidth: 100 }} className={styles.table_header_cell}>
          Category
        </td>
      )}
      <td style={{ minWidth: 80 }} className={styles.table_header_cell}>
        Org
      </td>
      <td style={{ minWidth: 80 }} className={styles.table_header_cell}>
        Fund
      </td>
      <td style={{ minWidth: 80 }} className={styles.table_header_cell}>
        Act / Proj
      </td>
      <td
        style={{ width: '50%', minWidth: 100 }}
        className={styles.table_header_cell}>
        Name
      </td>
      {!compact && (
        <td style={{ minWidth: 100 }} className={styles.table_header_cell}>
          Start Date
        </td>
      )}
      {!compact && (
        <td style={{ minWidth: 100 }} className={styles.table_header_cell}>
          End Date
        </td>
      )}
      {editable && <td></td>}
    </tr>
  );

  let colSpan =
    4 + (editable ? 1 : 0) + (!compact ? 3 : 0) + (onSelectAccount ? 1 : 0);
  let accountListContent =
    !accountList ||
    (accountList.length === 0 && (
      <tr>
        <td colSpan={colSpan}>
          <NoContentBox label={'No Accounts'} />
        </td>
      </tr>
    )) ||
    accountList.map((account, index) => {
      let isAccountSelected = selectedAccount === account;
      return (
        <tr
          key={index}
          className={isAccountSelected ? styles.selected_row : ''}>
          {onSelectAccount && (
            <td style={{ verticalAlign: 'middle', textAlign: 'center' }}>
              <Tooltip
                minimal
                content={
                  isAccountSelected
                    ? 'Deselect account to show all budgets'
                    : 'Select account to filter budgets'
                }>
                <Button
                  className={
                    isAccountSelected
                      ? styles.deselect_budget_button
                      : styles.select_budget_button
                  }
                  alignText={Alignment.CENTER}
                  minimal={compact || narrowScreen}
                  small
                  fill
                  icon={isAccountSelected ? 'small-cross' : 'bank-account'}
                  onClick={() => {
                    onSelectAccount(isAccountSelected ? undefined : account);
                  }}
                  text={
                    !compact &&
                    !narrowScreen &&
                    (isAccountSelected ? 'Show all' : 'Select Budget')
                  }
                />
              </Tooltip>
            </td>
          )}

          {!compact && (
            <td style={{ textAlign: 'center' }}>
              {editable && (
                <BOSSelect
                  defaultSelectedId={account?.category}
                  onItemSelect={(category) => {
                    updateItemValue(account, 'category', category?.key || null);
                  }}
                  listData={Object.entries(TRANSACTION_CATEGORY).map(
                    ([key, value]) => {
                      return {
                        key,
                        value,
                      };
                    },
                  )}
                  idField={'key'}
                  labelField={'value'}
                />
              )}
              {!editable && (
                <span className={isAccountSelected ? styles.selected_text : ''}>
                  {getTransactionCategory(account.category)}
                </span>
              )}
            </td>
          )}

          <td style={{ textAlign: 'center' }}>
            {editable && (
              <InputGroup
                type='text'
                style={{ textAlign: 'center' }}
                value={account.org || ''}
                readOnly={!editable}
                onChange={(event) =>
                  updateItemValue(account, 'org', event.target.value)
                }
              />
            )}
            {!editable && (
              <span className={isAccountSelected ? styles.selected_text : ''}>
                {account.org || ''}
              </span>
            )}
          </td>

          <td style={{ textAlign: 'center' }}>
            {editable && (
              <InputGroup
                type='text'
                style={{ textAlign: 'center' }}
                value={account.fund || ''}
                readOnly={!editable}
                onChange={(event) => {
                  updateItemValue(account, 'fund', event.target.value);
                }}
              />
            )}
            {!editable && (
              <span className={isAccountSelected ? styles.selected_text : ''}>
                {account.fund || ''}
              </span>
            )}
          </td>

          <td style={{ textAlign: 'center' }}>
            {editable && (
              <InputGroup
                type='text'
                style={{ textAlign: 'center' }}
                value={account.code || ''}
                readOnly={!editable}
                onChange={(event) => {
                  updateItemValue(account, 'code', event.target.value);
                }}
              />
            )}
            {!editable && (
              <span className={isAccountSelected ? styles.selected_text : ''}>
                {account.code || ''}
              </span>
            )}
          </td>

          <td style={{ textAlign: 'center' }}>
            {editable && (
              <InputGroup
                type='text'
                style={{ textAlign: 'center' }}
                value={account.name || ''}
                readOnly={!editable}
                onChange={(event) => {
                  updateItemValue(account, 'name', event.target.value);
                }}
              />
            )}
            {!editable && (
              <span className={isAccountSelected ? styles.selected_text : ''}>
                {account.name || ''}
              </span>
            )}
          </td>

          {!compact && (
            <td style={{ textAlign: 'center' }}>
              {editable && (
                <BOSDateInput
                  value={account.valid_from}
                  onChange={(date) =>
                    updateItemValue(account, 'valid_from', date)
                  }
                />
              )}
              {!editable && (
                <span className={isAccountSelected ? styles.selected_text : ''}>
                  {formatDate(account.valid_from)}
                </span>
              )}
            </td>
          )}

          {!compact && (
            <td style={{ textAlign: 'center' }}>
              {editable && (
                <BOSDateInput
                  value={account.valid_to}
                  onChange={(date) =>
                    updateItemValue(account, 'valid_to', date)
                  }
                  maxDate={addYears(new Date(), 20)}
                />
              )}
              {!editable && (
                <span className={isAccountSelected ? styles.selected_text : ''}>
                  {formatDate(account.valid_to)}
                </span>
              )}
            </td>
          )}

          {editable && (
            <td>
              <div style={{ display: 'flex' }}>
                <DeleteButton
                  miniVersion
                  onDeleteItem={() => deleteClicked(account)}
                  tooltipText={'Delete Account'}
                />

                <DuplicateButton
                  miniVersion
                  duplicateClickHandler={() => duplicateAccount(account)}
                  tooltipText={'Duplicate Account'}
                />
              </div>
            </td>
          )}
        </tr>
      );
    });

  const newButton = editable && (
    <AddNewButton
      clickHandler={ChargeAccountData.getBlank}
      buttonText='Add Account'
    />
  );

  const undoButton = editable && hasBeenUpdated && (
    <UndoButton clickHandler={undoChanges} />
  );

  const saveButton = editable && hasBeenUpdated && (
    <SaveButton clickHandler={saveClicked} />
  );

  const footer = (
    <ButtonFooter marginTop={0}>
      {undoButton}
      {newButton}
      {saveButton}
    </ButtonFooter>
  );

  return (
    <div>
      <table
        className={
          compact
            ? styles.account_list_table_compact
            : styles.account_list_table
        }>
        <thead>{headers}</thead>
        <tbody>{accountListContent}</tbody>
      </table>
      {footer}
    </div>
  );
};

AccountsTable.propTypes = {
  orgId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  selectedAccount: PropTypes.object,
  editable: PropTypes.bool,
  compact: PropTypes.bool,
  fieldsForNewAccount: PropTypes.object,
  onUpdateAccounts: PropTypes.func,
  onSelectAccount: PropTypes.func,
};

export default AccountsTable;
